/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { StudiesFilter, FilterOption, StudyTableMode } from './types'
import * as etmfApi from '../../../services/api/etmf'
import { useServiceContext } from '../../../root'
import { InfiniteScrollSelect } from '../../../components/InfiniteScrollSelect/InfiniteScrollSelect'

interface StudiesFilterPanelProps {
  mode: StudyTableMode
  onFilterChange: (fsiIds: number[]) => void
  isCollapsed: boolean
}

const STUDIES_FILTER_KEY = 'studiesKey'

export const StudiesFilterPanel: React.FC<StudiesFilterPanelProps> = ({ mode, onFilterChange, isCollapsed }) => {
  const { t } = useTranslation()
  const { service } = useServiceContext()
  const [filter, setFilter] = useState<StudiesFilter>({
    myEnvironments: null,
    myStudies: null
  })

  // Load persisted filter on mount
  useEffect(() => {
    const persisted = localStorage.getItem(STUDIES_FILTER_KEY)
    if (persisted) {
      try {
        const parsed = JSON.parse(persisted)
        setFilter(parsed)
        // Apply persisted filter
        const fsiIds = [
          ...(parsed.myEnvironments?.map((o: FilterOption) => o.value) || []),
          ...(parsed.myStudies?.map((o: FilterOption) => o.value) || [])
        ]
        if (fsiIds.length > 0) {
          onFilterChange(fsiIds)
        }
      } catch (error) {
        console.error('Failed to parse persisted filter:', error)
      }
    }
  }, [onFilterChange])

  const handleApplyFilter = useCallback(() => {
    // Persist filter
    localStorage.setItem(STUDIES_FILTER_KEY, JSON.stringify(filter))

    // Extract FSI IDs from selected options and convert strings to numbers
    const fsiIds = [
      ...(filter.myEnvironments?.map((o) => parseInt(o.value, 10)) || []),
      ...(filter.myStudies?.map((o) => parseInt(o.value, 10)) || [])
    ]

    onFilterChange(fsiIds)
  }, [filter, onFilterChange])

  const handleClearFilter = useCallback(() => {
    const clearedFilter: StudiesFilter = {
      myEnvironments: null,
      myStudies: null
    }
    setFilter(clearedFilter)
    localStorage.setItem(STUDIES_FILTER_KEY, JSON.stringify(clearedFilter))
    onFilterChange([])
  }, [onFilterChange])

  const loadEnvironmentOptions = async (search: string, page: number) => {
    const userId = service.auth.getUserId()
    if (!userId) {
      return { options: [], hasMore: false }
    }
    return etmfApi.getOptionsForAsyncPickerInfiniteScrollMyEnvironments(search || null, page, userId.toString())
  }

  const loadStudyOptions = async (search: string, page: number) => {
    const userId = service.auth.getUserId()
    if (!userId) {
      return { options: [], hasMore: false }
    }
    return etmfApi.getOptionsForAsyncPickerInfiniteScrollMyStudies(search || null, page, userId.toString())
  }

  // Don't render if NOT collapsed (collapsed=true means show in legacy)
  if (!isCollapsed) {
    return null
  }

  return (
    <div className="filter-wrapper">
      <div className="row">
        <div className="col-sm-6">
          {mode === 'environments' && (
            <div className="form-group">
              <label>{t('EMTF_MD_TRIALS')}</label>
              <InfiniteScrollSelect
                placeholder={t('ETMF_MD_TRIALS_PLACEHOLDER')}
                asyncLoadFn={loadEnvironmentOptions}
                onChange={(options) => setFilter({ ...filter, myEnvironments: options as FilterOption[] })}
                value={filter.myEnvironments || []}
                isMulti
                searchable
              />
            </div>
          )}

          {mode === 'studies' && (
            <div className="form-group">
              <label>{t('EMTF_MD_TRIALS')}</label>
              <InfiniteScrollSelect
                placeholder={t('ETMF_MD_TRIALS_PLACEHOLDER')}
                asyncLoadFn={loadStudyOptions}
                onChange={(options) => setFilter({ ...filter, myStudies: options as FilterOption[] })}
                value={filter.myStudies || []}
                isMulti
                searchable
              />
            </div>
          )}
        </div>

        <div className="col-sm-12">
          <div className="clearfix">
            <div className="pull-left">
              <button
                data-action="filter"
                className="btn btn-primary"
                onClick={handleApplyFilter}
                id="studies-filter-apply-btn"
              >
                {t('BTN_FILTER')}
              </button>
              <button
                data-action="reset"
                className="btn btn-link"
                onClick={handleClearFilter}
                id="studies-filter-clear-btn"
              >
                {t('BTN_CLEAR_FILTER')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
